import Anchor from '@jetbrains/ring-ui/components/dropdown/anchor'
import {Type} from '@jetbrains/ring-ui/components/list/consts'
import Select from '@jetbrains/ring-ui/components/select/select'
import classNames from 'classnames'
import * as React from 'react'

import {Policy} from '../../../rest/branches'
import type {Branch} from '../../../services/rest'
import type {BranchItem, RingSelectAnchorProps} from '../../../types'
import {DEFAULT_NAME, parseBranch} from '../../../utils/branchNames'
import {noop} from '../../../utils/empty'
import {notNull} from '../../../utils/guards'
import Icon from '../Icon/Icon'
import SvgIcon from '../SvgIcon/SvgIcon'
import {hasSelection} from '../Tree/Tree.utils'

import type {BranchSelectBaseProps, Props} from './BranchSelect.types'
import {createGroup, createItem, createSeparator, matches} from './BranchSelect.utils'

import styles from './BranchSelect.css'

export const Z_INDEX_INCREMENT = 5
const POPUP_WIDTH = 320
const ActiveBranches = createItem({
  name: '<Active branches>',
  active: true,
  wildcard: true,
})
const AllBranches = createItem({
  name: '<All branches>',
  wildcard: true,
})
const UnspecifiedBranch = createItem({
  name: '<Unspecified branch>',
  unspecified: true,
})
type DefaultProps = {
  hasNonDefaultBranches: boolean
  shouldBeVisible: boolean
  onSelect: (selected: Branch | null | undefined, replace?: boolean) => unknown
}
type InnerProps = Props<BranchSelectBaseProps> & DefaultProps

class BranchSelect extends React.PureComponent<InnerProps> {
  static defaultProps: DefaultProps = {
    shouldBeVisible: true,
    hasNonDefaultBranches: true,
    onSelect: noop,
  }

  newSelected: null | {
    value: BranchItem | null | undefined
  } = null

  componentDidMount() {
    this.resetIfNoBranches()

    if (this.newSelected != null) {
      this.select(this.newSelected.value, true)
      this.newSelected = null
    }
  }

  componentDidUpdate() {
    this.resetIfNoBranches()

    if (this.newSelected != null) {
      this.select(this.newSelected.value, true)
      this.newSelected = null
    }
  }

  handleSelect = (selected: BranchItem | null | undefined) => this.select(selected)
  customAnchor = ({wrapperProps, buttonProps, popup}: RingSelectAnchorProps): React.ReactNode => {
    const {onClick, ...restButtonProps} = buttonProps
    const {className, old, minimalistic, special} = this.props
    const {branch} = this.getBranches().selected ?? {}
    const isDefaultSelected = branch?.default === true
    const classes = classNames(className, wrapperProps.className, {
      [styles.old]: old,
    })
    const newButtonClasses = classNames(styles.newButton, {
      [styles.defaultSelected]: isDefaultSelected,
    })
    const oldButtonClasses = classNames(styles.oldButton, {
      [styles.minimalisticButton]: minimalistic,
      [styles.specialButton]: special,
      [styles.defaultSelected]: isDefaultSelected && !special,
    })
    const label = isDefaultSelected
      ? `Default branch${branch != null && branch.name !== DEFAULT_NAME ? ` (${branch.name})` : ''}`
      : buttonProps.children

    const onClickWrapper = () => {
      if (onClick != null && !hasSelection()) {
        onClick()
      }
    }

    return (
      <span {...wrapperProps} className={classes} data-branch-select>
        {old === true ? (
          <button
            type="button"
            {...restButtonProps}
            className={oldButtonClasses}
            onClick={onClickWrapper}
          >
            <span>
              <Icon className={styles.branchIcon} name="branch" />
            </span>
            <span className={styles.oldLabel}>{label}</span>
            <span>
              <Icon
                className={
                  special === true ? styles.dropdownTriggerSpecial : styles.dropdownTrigger
                }
                name={special === true ? 'dropdownTriggerSpecial' : 'dropdownTrigger'}
              />
            </span>
          </button>
        ) : (
          <Anchor
            {...buttonProps}
            text={false}
            className={newButtonClasses}
            onClick={onClickWrapper}
          >
            <SvgIcon icon="branches" className={styles.icon} />
            <span className={styles.newLabel}>{label}</span>
          </Anchor>
        )}
        {popup}
      </span>
    )
  }

  select(selected: BranchItem | null | undefined, replace?: boolean) {
    const branch = selected?.branch
    this.props.onSelect(branch?.wildcard === true ? null : branch, replace)
  }

  resetIfNoBranches() {
    const {selected, branches, hasNonDefaultBranches, onSelect} = this.props

    if (selected && branches && !hasNonDefaultBranches) {
      onSelect(null, true)
    }
  }

  getWildCard(): BranchItem | null | undefined {
    const {policy, excludeWildcard} = this.props

    if (excludeWildcard === true) {
      return null
    }

    return policy === Policy.ACTIVE_HISTORY_AND_ACTIVE_VCS_BRANCHES ? ActiveBranches : AllBranches
  }

  getBranches(): {
    branches: ReadonlyArray<BranchItem | null | undefined>
    selected: BranchItem | null | undefined
    disabled?: boolean
  } {
    const {
      branches: branchesAndGroups,
      selected,
      includeUnspecified,
      selectedIsKnown,
      allowAny,
      ready,
    } = this.props
    const wildcard = this.getWildCard()
    const unspecified = includeUnspecified === true ? UnspecifiedBranch : null

    if (!branchesAndGroups) {
      const selectedItem = createItem(selected)
      return {
        branches: [unspecified, wildcard, selectedItem],
        selected: selectedItem ?? wildcard ?? unspecified,
        disabled: true,
      }
    }

    const groups = branchesAndGroups.filter(branch => branch.groupFlag)
    const branches = branchesAndGroups.filter(branch => !branch.groupFlag)
    const defaultBranch = branches.find(branch => branch.default)
    const nonDefaultBranches = branches.filter(branch => !branch.default)
    const activeBranches = nonDefaultBranches.filter(branch => branch.active)
    const inactiveBranches = nonDefaultBranches.filter(branch => !branch.active)
    const defaultItem = createItem(defaultBranch)
    const branchItems = [
      unspecified,
      wildcard,
      defaultItem != null ? createSeparator('Default') : null,
      defaultItem,
      ...createGroup('Groups', groups),
      ...createGroup('Active', activeBranches),
      ...createGroup('Inactive', inactiveBranches),
    ]
    const defaultSelected = wildcard ?? defaultItem ?? unspecified

    if (!selected) {
      return {
        branches: branchItems,
        selected: defaultSelected,
      }
    }

    if (selected.unspecified === true) {
      return {
        branches: branchItems,
        selected: unspecified,
      }
    }

    const knownBranch = branchesAndGroups.find(branch => matches(branch, selected))

    if (knownBranch) {
      return {
        branches: branchItems,
        selected: createItem(knownBranch),
      }
    } else if ((selectedIsKnown === false || (selectedIsKnown == null && ready)) && !allowAny) {
      this.newSelected = {
        value: defaultSelected,
      }
      return {
        branches: branchItems,
        selected: defaultSelected,
      }
    } else {
      const selectedItem = createItem(selected)
      return {
        branches: [...branchItems, createSeparator('Unknown'), selectedItem],
        selected: selectedItem,
      }
    }
  }

  render(): React.ReactNode {
    const {className, currentZindex, allowAny} = this.props
    const {branches, selected, disabled} = this.getBranches()
    const data = branches.filter(notNull)
    return (
      <Select
        type={Select.Type.CUSTOM}
        customAnchor={this.customAnchor}
        add={allowAny ? {prefix: 'Use branch'} : undefined}
        onAdd={value => this.props.onSelect(parseBranch(value))}
        compact
        label="Select a branch"
        filter={{
          placeholder: 'Filter branches',
          fn: (item, string) => {
            if (item.rgItemType === Type.SEPARATOR) {
              const successors = data.slice(
                data.findIndex(otherItem => otherItem.key === item.key) + 1,
              )
              const children = successors.slice(
                0,
                successors.findIndex(successor => successor.rgItemType === Type.SEPARATOR),
              )
              return children.some(child => child.label?.toLowerCase().includes(string))
            }
            return item.label?.toLowerCase().includes(string) ?? false
          },
        }}
        selected={selected}
        data={data}
        onSelect={this.handleSelect}
        disabled={disabled}
        className={className}
        popupStyle={
          currentZindex != null
            ? {
                zIndex: currentZindex + Z_INDEX_INCREMENT,
              }
            : undefined
        }
        disableMoveOverflow
        disableScrollToActive
        minWidth={POPUP_WIDTH}
      />
    )
  }
}

export default BranchSelect
